import { Form } from "@inertiajs/react"
import { Trash2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { masterclassModulePath } from "@/routes"
import type { MasterclassModuleMode } from "@/types/masterclasses"

/**
 * OKS-55 — Module delete-confirm AlertDialog (UX Spec §4).
 *
 * Hard-delete via Inertia `<Form method="delete">` to
 * `masterclassModulePath(masterclassId, moduleId)`. The model declares
 * `dependent: :purge_later` on `has_one_attached :video` (TRD KD-10),
 * so a successful delete also enqueues an `ActiveStorage::PurgeJob` to
 * remove the blob — surfaced to the user via the dialog body copy
 * variant when `mode === "upload"`.
 *
 * UX Spec §6 a11y: default focus on Cancel; destructive button requires
 * deliberate tab. Same posture as the masterclass-level delete dialog.
 */
export interface MasterclassModuleDeleteDialogProps {
  masterclassId: number
  moduleId: number
  moduleTitle: string
  mode: MasterclassModuleMode
  /** Trigger element override — defaults to a destructive trash button. */
  triggerLabel?: string
  /** Render the trigger as an `IconButton`-sized action (for row contexts). */
  iconOnly?: boolean
}

export function MasterclassModuleDeleteDialog({
  masterclassId,
  moduleId,
  moduleTitle,
  mode,
  triggerLabel,
  iconOnly = false,
}: MasterclassModuleDeleteDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // Inline both `t()` calls in the ternary so the i18n health scanner
  // sees both literal keys at the call site.
  const body =
    mode === "upload"
      ? t("masterclasses.show.modules.delete_dialog.body_upload")
      : t("masterclasses.show.modules.delete_dialog.body_link")

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={
          iconOnly ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("masterclasses.show.modules.row.delete_aria", {
                title: moduleTitle,
              })}
              data-testid={`masterclass-module-delete-trigger-${moduleId}`}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </Button>
          ) : (
            <Button
              variant="destructive"
              data-testid={`masterclass-module-delete-trigger-${moduleId}`}
            >
              <Trash2 className="mr-1 size-4" aria-hidden="true" />
              {triggerLabel ?? t("masterclasses.show.actions.delete")}
            </Button>
          )
        }
      />
      <AlertDialogContent
        data-testid={`masterclass-module-delete-dialog-${moduleId}`}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("masterclasses.show.modules.delete_dialog.title", {
              title: moduleTitle,
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <Form
          action={masterclassModulePath(masterclassId, moduleId)}
          method="delete"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <AlertDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                autoFocus
                data-testid={`masterclass-module-delete-cancel-${moduleId}`}
              >
                {t("masterclasses.show.modules.delete_dialog.cancel")}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={processing}
                data-testid={`masterclass-module-delete-confirm-${moduleId}`}
              >
                {t("masterclasses.show.modules.delete_dialog.confirm")}
              </Button>
            </AlertDialogFooter>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
